import React, { useState } from "react";
import RowIntel from "./list/RowIntel";
import ChatRowIntel from "./list/ChatRowIntel";
import ConversationIntelFilters from "./list/ConversationIntelFilters";
import RailFilterChip from "./RailFilterChip";
import { activeIntelFilterCount, RAIL_SORTS } from "./list/conversationIntel";
import { outcomeAccentClass, outcomeHoverClass } from "./lib/outcomeStyles";
import { fmtDateLabel, fmtNumber } from "../shared/format";
import { SkeletonText } from "../shared/Skeleton";

const SearchIcon = () => (
  <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
  </svg>
);

// CONVERSATION RAIL — the permanent left spine (V6 §2, §E.15). "The list is the
// spine, not the appendix": conversations live in a fixed rail that never leaves,
// so there is no scroll position from which a reader cannot see and click one.
//
// This renders the rail's content — the total counter, the active-drill chip, and
// the scrolling row list. It is presentational: the page owns the fetch (one
// fn_get_conversation_list call, modality-scoped) and passes the rows in. The rail
// scrolls INDEPENDENTLY of the analysis column, which is why its scroll region
// carries `min-h-0 overflow-y-auto` — a flex/grid child will not scroll without the
// min-height reset (the same trap §B.1 calls out for the two page columns).
//
// A row shows the guest's name (or the number when anonymous), the date, and its
// RowIntel line (outcome, stay type, duration, recording, never-spoke). Clicking a
// row opens that conversation in the DrillOver.
//
// The rail carries the §E.15 controls: the total counter, the active-drill chip,
// search, the intelligence filters (caller-type default 'guest'), Clear, and the
// Date/Activity sort. DEFERRED to fast-follow (2026-09-02 scope ruling): Tags — a
// writeful per-conversation tagging subsystem (backend writes + AI suggestions)
// that needs a render-only design pass before it lands here — and CSV/PDF export
// (the on-demand jspdf pipeline that still lives on ProjectDetailPage).

const railName = (row) => {
  if (row?.visitor_name) return row.visitor_name;
  if (row?.name) return row.name;
  if (row?.phone) return row.phone;
  return "Unknown caller";
};

// The card's left-accent colour, and its hover treatment, reflect the conversation
// outcome (red abandoned / green reservation-assisted / blue else) — see
// ./lib/outcomeStyles, shared with the drill-set cards.

// Memoised so opening the drill drawer (or any unrelated page state change) does not
// re-render all ~150 conversation cards — each row only re-renders when its own row
// object, modality, or click handler actually changes.
const Row = React.memo(function Row({ row, modality, onRowClick }) {
  // fn_get_conversation_list returns the intel fields on the item itself, while
  // RowIntel/ChatRowIntel read them through intelOf(row) === row.intel — so the
  // item is wrapped as both the intel record and the conversation (the same shape
  // the drill levels pass). Chat rows carry question_count, which is why the two
  // pages use different row-intel components.
  const IntelLine = modality === "chat" ? ChatRowIntel : RowIntel;
  return (
    <button
      type="button"
      data-testid="rail-row"
      data-conversation-id={row.conversation_id}
      onClick={() => onRowClick && onRowClick(row)}
      className={`group w-full text-left rounded-lg border border-gray-200 dark:border-gray-700/60 border-l-[3px] ${outcomeAccentClass(row?.outcome)} bg-white dark:bg-gray-800/30 ${outcomeHoverClass(row?.outcome)} shadow-sm px-3 py-2.5 min-h-[44px] hover:shadow-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]`}
    >
      <div className="flex items-center justify-between gap-2">
        {/* Caller identifier (incl. the "Unknown caller" fallback) with a channel
            icon, and the date in the theme link blue — gi-link, the accessible
            token, matching the drill rows (user ruling 2026-09-04). */}
        <span className="flex items-center gap-1.5 min-w-0">
          <svg className="w-3.5 h-3.5 shrink-0 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {modality === "chat" ? (
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            ) : (
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
            )}
          </svg>
          <span className="gi-body font-medium gi-link truncate">{railName(row)}</span>
        </span>
        {row.started_at && (
          <span className="text-xs gi-link shrink-0">{fmtDateLabel(row.started_at)}</span>
        )}
      </div>
      <IntelLine row={{ intel: row, convo: row }} />
    </button>
  );
});

const SORT_LABELS = { date: "Date", activity: "Activity" };
// Link-style sort controls, each with an icon: a calendar for Date, an activity
// pulse for Activity.
const SORT_ICONS = {
  date: (<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>),
  activity: (<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>),
};

const ConversationRail = ({
  rows, total, loading = false, modality = "voice",
  activeFilter, onClearFilter, onRowClick,
  filters, onFiltersChange, onClearFilters, search, onSearchChange,
  sortBy = "date", onSortChange, onExportCsv,
}) => {
  const list = Array.isArray(rows) ? rows : [];
  const count = total != null ? total : list.length;
  const activeCount = activeIntelFilterCount(filters);
  // Advanced search: the intel dropdowns are hidden until asked for, so the rail
  // opens on search + results. A count badge on the toggle (and the always-visible
  // "Clear filters (N)") keeps an applied filter discoverable while collapsed —
  // note caller_type defaults to 'guest', so N is 1 out of the box.
  const [showFilters, setShowFilters] = useState(false);

  return (
    <div data-testid="conversation-rail" className="flex flex-col h-full min-h-0">
      {/* Header — the active-drill chip, sort, search, and the intel filters. The
          whole header is shrink-0; only the row list below scrolls. The total counter
          moved to the results title directly above the cards it counts. */}
      <div data-testid="rail-header" className="shrink-0 px-3 py-3 border-b border-gray-200 dark:border-gray-800 flex flex-col gap-2">
        {activeFilter && <RailFilterChip filter={activeFilter} onClear={onClearFilter} />}
        {onSortChange && (
          <div className="flex items-center justify-between gap-1" role="group" aria-label="Sort conversations">
            <span className="gi-label font-extrabold text-gray-800 dark:text-gray-100 mr-1">Sort</span>
            <div className="flex items-center gap-1">
              {RAIL_SORTS.map((key) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`rail-sort-${key}`}
                  aria-pressed={sortBy === key}
                  onClick={() => onSortChange(key)}
                  className={`inline-flex items-center gap-1.5 min-h-[44px] px-1 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] rounded ${
                    sortBy === key
                      ? "gi-link font-semibold underline underline-offset-4"
                      : "text-gray-500 dark:text-gray-400 hover:text-[#1a86c9] dark:hover:text-[#32B1FF]"
                  }`}
                >
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    {SORT_ICONS[key]}
                  </svg>
                  {SORT_LABELS[key]}
                </button>
              ))}
            </div>
          </div>
        )}
        {onSearchChange && (
          <label className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-800 px-2 min-h-[44px]">
            <SearchIcon />
            <input
              type="search"
              data-testid="rail-search"
              value={search || ""}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search conversations"
              className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
            />
          </label>
        )}
        {/* One controls row: the Advanced-search toggle on the left, Export on the
            right — so the export sits beside a control instead of alone on its own
            line. The intel dropdowns (hidden until the toggle opens them) drop in
            below this row. Each dropdown resets via its own "All" (caller type via
            "Guest"), so there is no separate clear. */}
        {(( filters && onFiltersChange) || onExportCsv) && (
          <div className="flex items-center justify-between gap-3">
            {filters && onFiltersChange ? (
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                aria-expanded={showFilters}
                data-testid="rail-advanced-toggle"
                className="inline-flex items-center gap-1.5 min-h-[44px] text-sm font-medium gi-link"
              >
                <svg className={`w-4 h-4 shrink-0 transition-transform ${showFilters ? "rotate-90" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 6 15 12 9 18" />
                </svg>
                Advanced search{activeCount > 0 ? ` (${activeCount})` : ""}
              </button>
            ) : <span />}
            {onExportCsv && (
              <button
                type="button"
                data-testid="rail-export-csv"
                onClick={onExportCsv}
                title="Export these conversations to CSV"
                className="shrink-0 inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-md border border-[#32B1FF]/40 bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10 text-sm font-medium gi-link hover:bg-[#32B1FF]/10 dark:hover:bg-[#32B1FF]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] transition-colors"
              >
                <DownloadIcon />
                Export CSV
              </button>
            )}
          </div>
        )}
        {showFilters && filters && onFiltersChange && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30 p-3">
            <ConversationIntelFilters
              filters={filters}
              onChange={onFiltersChange}
              modality={modality}
            />
          </div>
        )}
      </div>

      {/* Title for the results list — labels the scrolling card container below,
          carrying the total count beside it. */}
      <div data-testid="rail-list-title" className="shrink-0 px-3 pt-3 pb-2 flex items-baseline justify-between gap-2">
        <h3 className="gi-label font-extrabold text-gray-800 dark:text-gray-100">Conversations</h3>
        <span className="gi-body font-semibold tabular-nums text-gray-900 dark:text-gray-100">{fmtNumber(count)}</span>
      </div>

      {/* The rows — the ONE region that scrolls independently. `min-h-0` is what
          lets it scroll inside the flex column instead of pushing the page. */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {loading && list.length === 0 ? (
          <div className="p-3"><SkeletonText lines={8} lineClass="h-10" /></div>
        ) : list.length === 0 ? (
          <p className="p-4 gi-body text-gray-500 dark:text-gray-400">
            {activeFilter ? "No conversations match this filter." : "No conversations in this period."}
          </p>
        ) : (
          // Cards, not a divided list — each row is its own card, separated by a
          // gap (like the live-feed design), with a little padding around the stack.
          <div className="p-3 space-y-2">
            {list.map((row) => (
              <Row key={row.conversation_id || row.id} row={row} modality={modality} onRowClick={onRowClick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationRail;
